import { BigNumber } from '@ethersproject/bignumber'
import { useEffect, useState } from 'react'
import { useEthers } from './useEthers'
import { ChainId, CHAIN_ID_NAMES } from '../constants'
import { CurrencyValue, Token } from '../model'

export const COMPARATOR_INTERVAL_SECONDS = 60

export interface Exchange {
  name: string
}

export interface Quote {
  amountIn?: CurrencyValue
  amountOut?: CurrencyValue
  gas: BigNumber
  gasUsd: BigNumber
  savings: CurrencyValue
  savingsUsd: BigNumber
  cost: CurrencyValue
  costUsd: BigNumber
  price: BigNumber
  difference: BigNumber
}

export interface ExchangeQuote {
  exchange: Exchange
  quote: Quote
}

export type Comparisons = {
  [key: string]: ExchangeQuote[]
}

export type ChainComparisons = {
  [T in ChainId]?: Comparisons
}

export function useComparator(
  tokenIn?: Token,
  tokenOut?: Token,
  controlled?: 'first' | 'second',
  amount?: CurrencyValue
) {
  const { chainId = ChainId.Mainnet } = useEthers()
  const [chainComparisons, setChainComparisons] = useState<ChainComparisons>({})
  const [timeoutStartAt, setTimeoutStartAt] = useState<Date | null>(null)
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true)
  const [refreshDirty, setRefreshDirty] = useState<boolean>(false)

  let exchangeQuotes: ExchangeQuote[] | undefined
  if (tokenIn && tokenOut && controlled && amount) {
    const key = `${tokenIn.address}-${tokenOut.address}-${controlled}-${amount.toString()}`

    useEffect(() => {
      let timeoutId: ReturnType<typeof setTimeout> | null = null
      let cancelled = false

      function reset() {
        if (timeoutId) {
          clearTimeout(timeoutId)
          timeoutId = null
        }
        setTimeoutStartAt(null)
        setRefreshDirty(false)
      }

      function scheduleTimeout() {
        if (autoRefresh) {
          timeoutId = setTimeout(updateComparator, COMPARATOR_INTERVAL_SECONDS * 1000)
          setTimeoutStartAt(new Date())
        }
      }

      function clearResults() {
        setChainComparisons((chainComparisons) => {
          return {
            ...chainComparisons,
            [chainId]: {
              ...chainComparisons[chainId],
              [key]: undefined,
            },
          }
        })
      }

      async function updateComparator() {
        reset()
        if (chainId && tokenIn && tokenOut && controlled && amount) {
          if (amount.value.eq(0)) {
            clearResults()
          } else {
            try {
              const res = await fetch(
                `https://api.integral.link/api/v1/comparator?network=${CHAIN_ID_NAMES[chainId]}&tokenInAddress=${
                  tokenIn.address
                }&tokenOutAddress=${tokenOut.address}&${
                  controlled === 'first' ? 'amountIn' : 'amountOut'
                }=${amount.value.toString()}`
              )
              const json = await res.json()
              if (!cancelled) {
                if (json && json.success) {
                  setChainComparisons((chainComparisons) => {
                    return {
                      ...chainComparisons,
                      [chainId]: {
                        ...chainComparisons[chainId],
                        [key]: json.data.map((quote: any) => {
                          const {
                            amountIn,
                            amountOut,
                            gas,
                            gasUsd,
                            price,
                            cost,
                            costUsd,
                            savings,
                            savingsUsd,
                            difference,
                          } = quote.quote
                          return {
                            exchange: {
                              name: quote.exchange.name,
                            },
                            quote: {
                              ...(amountIn
                                ? { amountIn: new CurrencyValue(tokenIn, BigNumber.from(amountIn)) }
                                : { amountOut: new CurrencyValue(tokenOut, BigNumber.from(amountOut)) }),
                              gas: BigNumber.from(gas),
                              gasUsd: BigNumber.from(gasUsd),
                              savings: new CurrencyValue(amountIn ? tokenIn : tokenOut, BigNumber.from(savings)),
                              savingsUsd: BigNumber.from(savingsUsd),
                              cost: new CurrencyValue(amountIn ? tokenIn : tokenOut, BigNumber.from(cost)),
                              costUsd: BigNumber.from(costUsd),
                              price: BigNumber.from(price),
                              difference: BigNumber.from(difference),
                            },
                          }
                        }),
                      },
                    }
                  })
                }
                scheduleTimeout()
              }
            } catch (err) {
              console.error(err)
              if (!cancelled) {
                clearResults()
                scheduleTimeout()
              }
            }
          }
        }
      }
      if (refreshDirty) {
        clearResults()
      }
      if (autoRefresh || !chainComparisons[chainId] || !chainComparisons[chainId]![key]) {
        updateComparator()
      }
      return () => {
        cancelled = true
        reset()
      }
    }, [chainId, tokenIn.address, tokenOut.address, controlled, amount.value.toString(), autoRefresh, refreshDirty])

    if (chainId && chainComparisons[chainId] && chainComparisons[chainId]![key]) {
      exchangeQuotes = chainComparisons[chainId]![key]
    }
  }

  return {
    exchangeQuotes,
    timeoutStartAt,
    autoRefresh,
    toggleAutoRefresh: () => setAutoRefresh(!autoRefresh),
    refresh: () => setRefreshDirty(true),
  }
}
